import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ChevronRight, Clock, FileDown, Printer, Server, User } from 'lucide-react';
import Markdown, { extractHeadings } from '../components/Markdown';
import ApplianceDownloads from '../components/ApplianceDownloads';
import { GUIDES, fillPlaceholders, findGuide, visibleGuides } from '../guides';
import { useAuth } from '../context/AuthContext';

interface GuideContext {
  gatewayHost: string;
  gatewayPort: string;
  broker: { id: number; name: string; siteName: string; tenantName: string; packageFile: string } | null;
}

// Guide operative in-app (2026-09-18): procedure passo-passo, con i comandi da terminale, che
// prima vivevano fuori dal prodotto (documenti, chat). Indice su /guides, singola guida su
// /guides/:slug. Vedi frontend/src/guides/index.ts per contenuti e segnaposto.
export default function GuidesPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const guides = visibleGuides(user);
  // Guida riservata (es. Athena XDR, sospesa): per gli altri utenti non esiste nemmeno via URL.
  if (slug) return guides.some(g => g.slug === slug) ? <GuideDetail slug={slug} /> : <GuideDetail slug="__none__" />;
  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2 mb-1"><BookOpen size={20} /> Guides</h1>
      <p className="text-sm text-gray-500 mb-5">Step-by-step procedures for the people who set up and operate the platform on site. Commands are ready to paste.</p>
      <div className="grid gap-3 md:grid-cols-2">
        {guides.map(g => (
          <Link key={g.slug} to={`/guides/${g.slug}`} className="card p-5 hover:border-primary-300 hover:shadow transition-colors group">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-semibold text-gray-900 group-hover:text-primary-700">{g.title}</h2>
              <ChevronRight size={16} className="text-gray-300 group-hover:text-primary-500 mt-1 shrink-0" />
            </div>
            <p className="text-sm text-gray-600 mt-1.5">{g.summary}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><User size={12} /> {g.audience}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {g.minutes}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function GuideDetail({ slug }: { slug: string }) {
  const guide = findGuide(slug);
  const [params] = useSearchParams();
  const brokerId = params.get('brokerId');
  const [ctx, setCtx] = useState<GuideContext | null>(null);
  const [ctxError, setCtxError] = useState<string | null>(null);

  useEffect(() => {
    setCtxError(null);
    axios.get('/api/guides/context', { params: { brokerId: brokerId || undefined } })
      .then(res => setCtx(res.data.data))
      .catch(err => {
        setCtxError(err.response?.data?.error || 'Could not load the platform context; generic values are shown.');
        // Il fallback: host di questa stessa console, porta di default. Meglio di un segnaposto.
        setCtx({ gatewayHost: window.location.hostname, gatewayPort: '6514', broker: null });
      });
  }, [brokerId]);

  const source = useMemo(() => {
    if (!guide) return '';
    const values: Record<string, string> = {
      ...guide.defaults,
      gateway_host: ctx?.gatewayHost || window.location.hostname,
      gateway_port: ctx?.gatewayPort || '6514',
    };
    if (ctx?.broker) {
      values.package_file = ctx.broker.packageFile;
      values.broker_name = ctx.broker.name;
      values.tenant_name = ctx.broker.tenantName;
      values.site_name = ctx.broker.siteName;
    }
    return fillPlaceholders(guide.content, values);
  }, [guide, ctx]);

  const headings = useMemo(() => extractHeadings(source).filter(h => h.level === 2), [source]);
  // Varianti della stessa procedura (es. broker su VM esistente / da immagine): il selettore
  // passa dall'una all'altra conservando ?brokerId, quindi la personalizzazione.
  const variants = guide?.variant ? GUIDES.filter(g => g.contextOf === guide.contextOf && g.variant) : [];

  if (!guide) {
    return (
      <div className="card p-8 text-center text-sm text-gray-500">
        Guide not found. <Link to="/guides" className="text-primary-700 underline">Back to the list</Link>
      </div>
    );
  }

  return (
    <div>
      <nav className="text-xs text-gray-500 mb-3 print:hidden">
        <Link to="/guides" className="hover:text-primary-700">Guides</Link> <span className="mx-1">/</span> {guide.title}
      </nav>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{guide.title}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1"><User size={12} /> {guide.audience}</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {guide.minutes}</span>
          </div>
        </div>
        <div className="flex gap-2 print:hidden">
          {guide.pdf && (
            <a href={guide.pdf} download className="btn-secondary text-sm inline-flex items-center gap-1.5"
              title="Ready-made PDF with example values (Contoso / Milano), not personalised">
              <FileDown size={14} /> Download PDF
            </a>
          )}
          <button onClick={() => window.print()} className="btn-secondary text-sm inline-flex items-center gap-1.5">
            <Printer size={14} /> Print / PDF
          </button>
        </div>
      </div>

      {variants.length > 1 && (
        <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm print:hidden">
          {variants.map(v => (
            <Link key={v.slug} to={`/guides/${v.slug}${brokerId ? `?brokerId=${brokerId}` : ''}`}
              className={`px-3 py-1.5 rounded-md ${v.slug === guide.slug ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
              {v.variant}
            </Link>
          ))}
        </div>
      )}

      {ctx?.broker && (
        <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-900 inline-flex items-center gap-2">
          <Server size={14} />
          <span>Personalised for broker <strong>{ctx.broker.name}</strong> — {ctx.broker.tenantName}, {ctx.broker.siteName}. File names and addresses below are the real ones.</span>
        </div>
      )}
      {guide.slug === 'broker-install-image' && <ApplianceDownloads />}
      {ctxError && <div className="mb-4 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 print:hidden">{ctxError}</div>}

      <div className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-8">
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Steps</div>
            <ol className="space-y-0.5">
              {headings.map(h => (
                <li key={h.id}>
                  {/* Con HashRouter un href="#id" cambia la route (pagina vuota): si scorre alla
                      sezione a mano, l'href resta per "apri in nuova scheda" e accessibilità. */}
                  <a href={`#${h.id}`} onClick={e => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="block rounded px-2 py-1 text-[13px] text-gray-600 hover:bg-primary-50 hover:text-primary-800 leading-snug">{h.text}</a>
                </li>
              ))}
            </ol>
          </div>
        </aside>
        <div className="card px-6 py-5 max-w-3xl min-w-0 print:border-0 print:shadow-none">
          {ctx ? <Markdown source={source} /> : <p className="text-sm text-gray-400">Loading…</p>}
        </div>
      </div>
    </div>
  );
}
